import React, { useContext, useEffect, useState } from "react";
import axios from "../api/axios";
import { PreferencesContext } from "../context/PreferencesContext";
import { useMessage } from "../context/MessageContext";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
} from "@mui/material";
import CommonTable from "../components/CommonTable";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value || 0);

const TeacherSetsReport = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { showMessage } = useMessage();

  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ totalQty: 0, totalReferenceValue: 0 });

  useEffect(() => {
    if (!organization || !academicYear) return;
    Promise.all([
      axios.get(`/branches?orgId=${organization}`),
      axios.get(`/vendors?orgId=${organization}&academicYear=${academicYear}`),
    ])
      .then(([bRes, vRes]) => {
        setBranches(bRes.data || []);
        setVendors(vRes.data || []);
      })
      .catch(() => showMessage("Failed to load filters", "error"));
  }, [organization, academicYear, showMessage]);

  useEffect(() => {
    if (!organization || !academicYear) return;
    const params = new URLSearchParams({
      organization,
      academicYear,
    });
    if (branchFilter) params.set("branch", branchFilter);
    if (vendorFilter) params.set("vendor", vendorFilter);

    axios
      .get(`/inventory/teacher-sets/summary?${params.toString()}`)
      .then((res) => {
        setRows(res.data?.rows || []);
        setTotals(
          res.data?.totals || { totalQty: 0, totalReferenceValue: 0 }
        );
      })
      .catch(() => showMessage("Failed to load teacher set report", "error"));
  }, [organization, academicYear, branchFilter, vendorFilter, showMessage]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Teacher Sets Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reference copies sent to branches for teachers. These are excluded from
        student sales stock. Use this report to reconcile complimentary copies
        with vendor billing.
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Branch</InputLabel>
          <Select
            value={branchFilter}
            label="Branch"
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>All branches</em>
            </MenuItem>
            {branches
              .filter((b) => b.code !== "CENTRAL")
              .map((b) => (
                <MenuItem key={b._id} value={b._id}>
                  {b.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Vendor</InputLabel>
          <Select
            value={vendorFilter}
            label="Vendor"
            onChange={(e) => setVendorFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>All vendors</em>
            </MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v._id} value={v._id}>
                {v.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, minWidth: 180 }}>
          <Typography variant="caption" color="text.secondary">
            Total teacher sets at branches
          </Typography>
          <Typography variant="h6">{totals.totalQty}</Typography>
        </Paper>
        <Paper sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="caption" color="text.secondary">
            Reference value (cost/price × qty)
          </Typography>
          <Typography variant="h6">
            {formatCurrency(totals.totalReferenceValue)}
          </Typography>
        </Paper>
      </Stack>

      <CommonTable
        rows={rows}
        exportable
        downloadFileName="teacher_sets_report"
        columns={[
          { label: "Branch", field: "branchName" },
          { label: "Vendor", field: "vendorName" },
          {
            label: "Item / Set",
            renderCell: (r) =>
              `${r.itemModel === "Collection" ? "[Set] " : ""}${r.itemName}`,
          },
          { label: "Qty Sent", field: "quantity" },
          {
            label: "Unit Cost",
            renderCell: (r) => formatCurrency(r.unitCost ?? r.unitPrice),
          },
          {
            label: "Reference Value",
            renderCell: (r) => formatCurrency(r.referenceValue),
          },
        ]}
      />
    </Box>
  );
};

export default TeacherSetsReport;
